import { ReadonlyVec2, vec2 } from 'gl-matrix';

/**
 * Compute the signed area of a polygon using the shoelace
 * (trapezoid) formula. Returns positive for CW winding and
 * negative for CCW winding in Y-up coordinate space.
 */
export function computeSignedArea(polygon: readonly ReadonlyVec2[]): number {
    const n = polygon.length;
    if (n < 3) {
        return 0;
    }
    let sum = 0;
    for (let i = 1; i < n; i++) {
        sum +=
            (polygon[i][0] - polygon[i - 1][0]) *
            (polygon[i][1] + polygon[i - 1][1]);
    }
    // Close the loop: last vertex -> first vertex
    sum +=
        (polygon[0][0] - polygon[n - 1][0]) *
        (polygon[0][1] + polygon[n - 1][1]);
    return sum;
}

/**
 * Determine whether a point lies inside a polygon using the
 * ray-casting (crossing number) algorithm.
 */
export function isPointInsidePolygon(
    point: ReadonlyVec2,
    polygon: readonly ReadonlyVec2[]
): boolean {
    const n = polygon.length;
    if (n < 3) {
        return false;
    }
    const px = point[0];
    const py = point[1];
    let inside = false;
    let prevX = polygon[n - 1][0];
    let prevY = polygon[n - 1][1];
    for (let k = 0; k < n; k++) {
        const x = polygon[k][0];
        const y = polygon[k][1];
        if (y > py !== prevY > py) {
            const xCross = prevX + ((py - prevY) * (x - prevX)) / (y - prevY);
            if (xCross < px) {
                inside = !inside;
            }
        }
        prevX = x;
        prevY = y;
    }
    return inside;
}

/** Per ring: how many rings contain it, and the smallest-area one that does (or null). */
export interface RingNesting {
    readonly depth: readonly number[];
    readonly parent: readonly (number | null)[];
}

/** Twice the signed area of triangle a, b, c: positive when counter-clockwise in Y-up space. */
export function orientation(
    a: ReadonlyVec2,
    b: ReadonlyVec2,
    c: ReadonlyVec2
): number {
    return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
}

/**
 * The convex hull of `points` (monotone chain), without repeated or collinear
 * points.
 */
export function convexHull<P extends ReadonlyVec2>(points: readonly P[]): P[] {
    const sorted = points.toSorted((p, q) => p[0] - q[0] || p[1] - q[1]);
    const distinct = sorted.filter(
        (p, i) =>
            i === 0 || p[0] !== sorted[i - 1][0] || p[1] !== sorted[i - 1][1]
    );
    if (distinct.length < 3) {
        return distinct;
    }
    const hull: P[] = [];
    const extend = (point: P, floor: number) => {
        while (
            hull.length > floor &&
            orientation(hull[hull.length - 2], hull[hull.length - 1], point) <=
                0
        ) {
            hull.pop();
        }
        hull.push(point);
    };
    distinct.forEach((point) => extend(point, 1));
    const lowerLength = hull.length;
    for (let i = distinct.length - 2; i >= 0; i--) {
        extend(distinct[i], lowerLength);
    }
    // The upper chain ends where the lower one began.
    hull.pop();
    return hull;
}

/** Whether an edge of `a` crosses an edge of `b` at a point interior to both. */
function ringEdgesCross(
    a: readonly ReadonlyVec2[],
    b: readonly ReadonlyVec2[]
): boolean {
    for (let i = 0, pi = a.length - 1; i < a.length; pi = i++) {
        for (let j = 0, pj = b.length - 1; j < b.length; pj = j++) {
            const [p1, p2, q1, q2] = [a[pi], a[i], b[pj], b[j]];
            const d1 = orientation(p1, p2, q1) * orientation(p1, p2, q2);
            if (
                d1 < 0 &&
                orientation(q1, q2, p1) * orientation(q1, q2, p2) < 0
            ) {
                return true;
            }
        }
    }
    return false;
}

/**
 * Ring `i` lies inside ring `j` when its absolute area is strictly smaller,
 * all its vertices are inside `j` and no edge properly crosses an edge of `j`,
 * so partially overlapping or repeated rings are not nested; among equal-area
 * containers the lowest index is the parent.
 */
export function ringNesting(
    rings: readonly (readonly ReadonlyVec2[])[]
): RingNesting {
    const areas = rings.map((ring) => Math.abs(computeSignedArea(ring)));
    const depth: number[] = [];
    const parent: (number | null)[] = [];
    rings.forEach((ring, i) => {
        const containers = rings
            .map((_, j) => j)
            .filter(
                (j) =>
                    areas[i] < areas[j] &&
                    ring.every((v) => isPointInsidePolygon(v, rings[j])) &&
                    !ringEdgesCross(ring, rings[j])
            );
        depth.push(containers.length);
        parent.push(
            containers.reduce<number | null>(
                (best, j) =>
                    best === null || areas[j] < areas[best] ? j : best,
                null
            )
        );
    });
    return { depth, parent };
}

/**
 * Groups rings into parts by nesting depth, not winding: a ring inside an
 * even number of rings starts a part, followed by the rings inside an odd
 * number whose smallest container it is. Rings of fewer than 3 vertices are
 * dropped. The rings must not cross or touch, so one vertex decides
 * containment; {@link ringNesting} handles rings that may.
 */
export function groupRingsIntoParts<Ring extends readonly ReadonlyVec2[]>(
    rings: readonly Ring[]
): Ring[][] {
    const usable = rings.filter((ring) => ring.length >= 3);
    const areas = usable.map((ring) => Math.abs(computeSignedArea(ring)));
    const containersOf = usable.map((ring, i) =>
        usable.flatMap((other, j) =>
            j !== i && isPointInsidePolygon(ring[0], other) ? [j] : []
        )
    );
    const parts = usable.map((ring) => [ring]);
    containersOf.forEach((containers, i) => {
        if (containers.length % 2 === 1) {
            const owner = containers.reduce((smallest, j) =>
                areas[j] < areas[smallest] ? j : smallest
            );
            parts[owner].push(usable[i]);
        }
    });
    return parts.filter((_, i) => containersOf[i].length % 2 === 0);
}

/**
 * Normalize winding order for a set of polygons so that outer
 * contours are CW and holes (odd nesting depth) are CCW.
 *
 * Does NOT mutate the input arrays; returns new arrays with
 * vertices in the corrected order.
 *
 * The winding convention matches the SDF generator expectation:
 * CW outer contours (positive signed area) and CCW holes
 * (negative signed area).
 */
export function normalizeWindingOrder(polygons: ReadonlyVec2[][]): vec2[][] {
    if (polygons.length === 0) {
        return [];
    }

    // Step 1: Ensure every polygon is CW (positive signed area).
    // Keep track of the planar vertices for containment checks.
    const cwPolygons: vec2[][] = polygons.map((poly) => {
        const copy = poly.map((v) => vec2.fromValues(v[0], v[1]));
        if (computeSignedArea(copy) < 0) {
            copy.reverse();
        }
        return copy;
    });

    if (cwPolygons.length === 1) {
        return cwPolygons;
    }

    // Step 2: Determine nesting depth via point-in-polygon.
    // Polygons at odd nesting depth are holes and need CCW winding.
    const result: vec2[][] = cwPolygons.map((poly, i) => {
        let containmentCount = 0;
        const ref = poly[0];
        for (let j = 0; j < cwPolygons.length; j++) {
            if (i === j) {
                continue;
            }
            if (isPointInsidePolygon(ref, cwPolygons[j])) {
                containmentCount++;
            }
        }
        if (containmentCount & 1) {
            // Odd containment = hole: reverse to CCW
            const reversed = poly.slice().reverse();
            return reversed;
        }
        return poly;
    });

    return result;
}
